import React from 'react'
import { Gavel, TrendingUp, ShieldAlert, Repeat, Compass } from 'lucide-react'
import Badge from './ui/Badge'
import { COMMITTEE } from '../config/ondsConfig'
import { formatPercent, formatPrice } from '../utils/format'
import { actionMeta, actionColor } from '../utils/tone'

const memberName = (id) => COMMITTEE.find((m) => m.id === id)?.name ?? id

export default function VerdictPanel({ verdict }) {
  if (!verdict?.action) return null
  const meta = actionMeta(verdict.action)
  const color = actionColor(verdict.action)

  return (
    <div
      className="onds-card p-5 md:p-7 relative overflow-hidden"
      style={{ borderColor: `${color}66` }}
    >
      <div
        className="absolute inset-0 opacity-[0.07] pointer-events-none"
        style={{ background: `radial-gradient(600px 200px at 50% 0%, ${color}, transparent 70%)` }}
      />
      <div className="relative">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-5">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-2xl flex items-center justify-center" style={{ backgroundColor: `${color}22`, border: `1px solid ${color}55` }}>
              <Gavel className="h-6 w-6" style={{ color }} />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-[var(--onds-text-dim)]">Committee Verdict</div>
              <h2 className="text-2xl font-bold">{meta.label}</h2>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {verdict.priceTarget != null && (
              <div className="text-right">
                <div className="text-[11px] uppercase tracking-wider text-[var(--onds-text-dim)]">Target{verdict.targetTimeframe ? ` · ${verdict.targetTimeframe}` : ''}</div>
                <div className="text-xl font-bold tabular-nums" style={{ color }}>{formatPrice(verdict.priceTarget)}</div>
              </div>
            )}
            <div className="text-right">
              <div className="text-[11px] uppercase tracking-wider text-[var(--onds-text-dim)]">Consensus</div>
              <div className="text-xl font-bold" style={{ color }}>{formatPercent(verdict.consensusRatio, 0)}</div>
            </div>
            <Badge tone={meta.tone} filled>{meta.label}</Badge>
          </div>
        </div>

        <p className="text-lg font-semibold leading-snug mb-4" style={{ color }}>
          {verdict.headline}
        </p>

        <div className="grid md:grid-cols-2 gap-4">
          <Section iconNode={<TrendingUp className="h-4 w-4" style={{ color: 'var(--onds-accent)' }} />} title="Position Orders">
            <p className="text-sm text-[var(--onds-text-dim)] leading-relaxed">{verdict.sizing}</p>
          </Section>
          <Section iconNode={<ShieldAlert className="h-4 w-4" style={{ color: 'var(--onds-accent)' }} />} title="Rationale">
            <p className="text-sm text-[var(--onds-text-dim)] leading-relaxed">{verdict.rationale}</p>
          </Section>
        </div>

        {verdict.reversalTrigger && (
          <div className="mt-4 rounded-xl p-4" style={{ backgroundColor: 'var(--onds-surface)', border: `1px solid ${color}44` }}>
            <div className="flex items-center gap-2 mb-1">
              <Repeat className="h-4 w-4" style={{ color }} />
              <span className="text-[11px] uppercase tracking-wider text-[var(--onds-text-dim)]">Next Move · What Reverses This</span>
            </div>
            <p className="text-sm text-[var(--onds-text-dim)] leading-relaxed">{verdict.reversalTrigger}</p>
          </div>
        )}

        {verdict.exitHunt && (
          <div className="mt-4 rounded-xl p-4" style={{ backgroundColor: 'var(--onds-surface)', border: `1px solid ${color}44` }}>
            <div className="flex items-center gap-2 mb-1">
              <Compass className="h-4 w-4" style={{ color }} />
              <span className="text-[11px] uppercase tracking-wider text-[var(--onds-text-dim)]">
                Exit Hunt · Where To Redeploy{verdict.rotateTarget ? ` · ${verdict.rotateTarget}` : ''}
              </span>
            </div>
            <p className="text-sm text-[var(--onds-text-dim)] leading-relaxed">{verdict.exitHunt}</p>
          </div>
        )}

        <div className="mt-4 rounded-xl p-4" style={{ backgroundColor: 'var(--onds-surface)', border: '1px dashed var(--onds-border)' }}>
          <div className="text-[11px] uppercase tracking-wider text-[var(--onds-text-dim)] mb-1">Goal Lens · Net Target</div>
          <p className="text-sm">{verdict.goalNote}</p>
        </div>

        {verdict.dissent?.length > 0 && (
          <div className="mt-4">
            <div className="text-[11px] uppercase tracking-wider text-[var(--onds-text-dim)] mb-2">Dissenting Voices</div>
            <div className="flex flex-col gap-2">
              {verdict.dissent.map((d, i) => (
                <div key={i} className="text-sm flex gap-2">
                  <span className="font-semibold shrink-0">{memberName(d.memberId)}:</span>
                  <span className="text-[var(--onds-text-dim)]">{d.note}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function Section({ iconNode, title, children }) {
  return (
    <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--onds-surface)' }}>
      <div className="flex items-center gap-2 mb-2">
        {iconNode}
        <span className="text-sm font-semibold">{title}</span>
      </div>
      {children}
    </div>
  )
}
